import * as d3 from 'd3';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useSentenceStore } from './provider/SentenceProvider';
import _ from 'lodash';

type LineCoords = {
    // 시작점 (청크 하단 중앙) - viewport 기준
    startX: number;
    startY: number;
    // 끝점 (학습노트 상단 중앙) - viewport 기준
    endX: number;
    endY: number;
};

const LearningNote = observer((): React.ReactNode => {
    const sentenceStore = useSentenceStore();
    const svgRef = useRef<SVGSVGElement>(null);
    const [coords, setCoords] = useState<LineCoords | null>(null);

    const sentence = sentenceStore.sentence;
    const learningNoteChunkIndexes = sentenceStore.learningNoteChunkIndexes;
    const currentLearningNoteChunkIndex = sentenceStore.currentLearningNoteChunkIndex;

    // 좌표 계산
    useEffect(() => {
        if (currentLearningNoteChunkIndex < 0) {
            setCoords(null);
            return;
        }

        const calculateCoords = () => {
            const chunkEl = document.querySelector(
                `[data-sentence-chunk-index="${currentLearningNoteChunkIndex}"]`,
            );
            // DOM에서 현재 인덱스의 노트를 직접 찾음
            const noteEl = document.querySelector(
                `[data-learning-note-index="${currentLearningNoteChunkIndex}"]`,
            );

            if (!chunkEl || !noteEl) {
                setCoords(null);
                return;
            }

            const chunkRect = chunkEl.getBoundingClientRect();
            const noteRect = noteEl.getBoundingClientRect();

            setCoords({
                startX: chunkRect.left + chunkRect.width / 2,
                startY: chunkRect.bottom,
                endX: noteRect.left + noteRect.width / 2,
                endY: noteRect.top,
            });
        };

        const debouncedCalculateCoords = _.debounce(calculateCoords, 500);

        const onResize = () => {
            // 그리기 전에 초기화
            d3.select(svgRef.current).selectAll('*').remove();
            debouncedCalculateCoords();
        };

        // 초기 계산 + 리사이즈/스크롤 대응
        debouncedCalculateCoords();

        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onResize, true);

        return () => {
            setCoords(null);
            debouncedCalculateCoords.cancel();
            window.removeEventListener('resize', debouncedCalculateCoords);
            window.removeEventListener('scroll', debouncedCalculateCoords, true);
        };
    }, [currentLearningNoteChunkIndex]);

    // d3로 path 그리기 및 애니메이션
    useEffect(() => {
        if (!svgRef.current) {
            return;
        }

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // 기존 요소 제거

        if (!coords) {
            return;
        }

        const { startX, startY, endX, endY } = coords;

        // 베지어 곡선 path 생성
        const midY = (startY + endY) / 2;
        const pathData = `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;

        // Path 그리기
        const path = svg
            .append('path')
            .attr('class', 'stroke-primary/60')
            .attr('d', pathData)
            .attr('fill', 'none')
            .attr('stroke-width', 2);

        // Path 애니메이션 (stroke-dasharray)
        //
        // 선이 그려지는 것 같은 효과를 주기 위한 편법
        // - stroke-dasharray: 점선의 선부분과 공간부분의 길이
        //   - 예: '2' -> 선부분 2, 공간부분 2
        //   - 예: '2 4' -> 선부분 2, 공간부분 4
        // - stroke-dashoffset: 점선의 시작의 오프셋
        //   - 예: 0 -> 시작점이 선의 시작점과 일치
        //   - 예: 10 -> 시작점이 선의 시작점에서 10px 떨어진 곳에서 시작
        //
        // 즉, 점선 크기를 전체 선 길이만큼 할당하고, 오프셋으로 전체 선 길이만큼을 밀어둔 후에,
        // 트랜지션으로 오프셋을 0으로 변경하면, 선이 그려지는 것 같은 효과를 줄 수 있다.
        const pathLength = path.node()?.getTotalLength() || 0;
        path.attr('stroke-dasharray', pathLength)
            .attr('stroke-dashoffset', pathLength)
            .transition()
            .duration(400)
            .ease(d3.easeQuadOut)
            .attr('stroke-dashoffset', 0);
    }, [coords]);

    useEffect(() => {
        // 학습 노트가 잘 보이게 스크롤
        if (currentLearningNoteChunkIndex >= 0) {
            const noteEl = document.querySelector(
                `[data-learning-note-index="${currentLearningNoteChunkIndex}"]`,
            );
            noteEl?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentLearningNoteChunkIndex]);

    return (
        <div
            className={twMerge(
                'relative mt-20 w-full',
                // 러닝 노트가 있을 때와 없을 때 문장 높이 차이가 적도록 최소 높이를 설정
                'min-h-24',
            )}
        >
            {/* 연결선 SVG - fixed로 viewport 전체 커버 */}
            {currentLearningNoteChunkIndex >= 0 && (
                <svg
                    ref={svgRef}
                    className={twMerge('stroke-primary pointer-events-none fixed inset-0', 'z-20')}
                    style={{ width: '100vw', height: '100vh' }}
                />
            )}
            {/* 모든 노트를 미리 렌더링하고 opacity로 표시/숨김 처리 */}
            {learningNoteChunkIndexes.map((chunkIndex, arrayIndex) => {
                const isVisible = chunkIndex === currentLearningNoteChunkIndex;
                const note = sentence?.chunks[chunkIndex]?.learningNote;

                return (
                    <div
                        key={chunkIndex}
                        className={twMerge(
                            'flex w-full items-center justify-center',
                            'transition-opacity duration-200',
                            // 첫 번째 노트만 static, 나머지는 겹치게 absolute
                            arrayIndex === 0 ? 'relative' : 'absolute inset-0',
                            isVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
                        )}
                    >
                        <div
                            data-learning-note-index={chunkIndex}
                            className={twMerge(
                                'text-primary bg-primary/10 max-w-150 rounded-lg p-5 text-sm',
                                'border-2 border-transparent', // 레이아웃 시프트 방지하기 위해 투명 보더 넣어줌
                                isVisible && 'border-primary/60',
                            )}
                        >
                            {note}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export default LearningNote;
